import React, {ReactNode} from 'react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  getRefundType,
  RefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {CancellationPenalty} from '@vio/core/src/offer/types/offer'

import {Price} from '../../search/Price'
import {NonRefundableInfo, NoRefundPeriod} from './NonRefundable'
import {PenaltyRowItem} from './PenaltyRowItem'
import {NUMBER_OF_DECIMALS_FOR_PRICE} from './utils'

interface BaseOfferProps {
  /** Total value of the offer price */
  totalPrice: number
  /** The display currency code of the offer price */
  displayCurrencyCode: string
  /** The hotel's time zone */
  hotelTimeZone?: string
  /** Replaces the offer total's currency figure, e.g. with the price in loyalty points */
  renderTotalPrice?: (value: number, currencyCode: string) => ReactNode
}

interface BookingContext {
  /** If charge-later refund policy should be shown */
  hasChargeLaterRefundPolicy?: boolean
  /** If this is after the booking has been made */
  isPostBooking?: boolean
  /** Whether this is an authenticate later booking */
  isAuthenticateLater?: boolean | null
}

export interface CancellationPenaltiesProps
  extends BaseOfferProps,
    BookingContext {
  cancellationPenalties: CancellationPenalty[]
}

export interface PenaltyHeaderProps
  extends Pick<BaseOfferProps, 'renderTotalPrice'> {
  refundType: RefundType
  totalPrice: number
  currencyCode: string
}

export interface PenaltyListProps extends BaseOfferProps, BookingContext {
  refundType: RefundType
  cancellationPenalties: CancellationPenalty[]
}

export interface PenaltyDisclaimerProps extends BookingContext {
  refundType: RefundType
}

const HeaderContainer = styled(Grid)`
  padding-top: 0;
  padding-bottom: ${cssTheme.layout.spacing.s300};
  &.--is-non-refundable {
    padding-top: ${cssTheme.layout.spacing.s300};
  }
`

const DisclaimerIcon = styled(Text)`
  margin-top: ${cssTheme.layout.spacing.s100};
`

const DisclaimerInfoWrapper = styled.div`
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

const PenaltyHeader = ({
  refundType,
  totalPrice,
  currencyCode,
  renderTotalPrice
}: PenaltyHeaderProps) => {
  if (refundType === RefundType.nonRefundable) {
    return (
      <HeaderContainer
        justify="space-between"
        direction="row"
        container
        spacing="s000"
        className="--is-non-refundable"
      >
        <Text variant="labelM">
          <FormattedMessageWrapper
            id="cancellationPenalties.nonRefundable"
            defaultMessage="Non-refundable"
          />
        </Text>
        <div data-id="nonRefundableTotalPrice">
          {renderTotalPrice ? (
            renderTotalPrice(totalPrice, currencyCode)
          ) : (
            <Price
              maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
              minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
              value={totalPrice}
              currency={currencyCode}
              size="md"
              variant="regular"
            />
          )}
        </div>
      </HeaderContainer>
    )
  }
  return (
    <HeaderContainer>
      <Text variant="labelM">
        <FormattedMessageWrapper
          id="cancellationPenalties.heading"
          defaultMessage="How much will it cost to cancel?"
        />
      </Text>
    </HeaderContainer>
  )
}

const PenaltyList = ({
  refundType,
  cancellationPenalties,
  totalPrice,
  displayCurrencyCode,
  hotelTimeZone,
  hasChargeLaterRefundPolicy,
  isAuthenticateLater,
  isPostBooking,
  renderTotalPrice
}: PenaltyListProps) => {
  if (refundType === RefundType.nonRefundable) {
    return <NonRefundableInfo isPostBooking={isPostBooking} />
  }

  return (
    <>
      {cancellationPenalties.map((penalty, index) => (
        <PenaltyRowItem
          key={penalty.start}
          id={index}
          penalty={penalty}
          totalOfferPrice={totalPrice}
          hotelTimeZone={hotelTimeZone}
          hasChargeLaterRefundPolicy={hasChargeLaterRefundPolicy}
          displayCurrencyCode={displayCurrencyCode}
          isAuthenticateLater={isAuthenticateLater}
        />
      ))}

      <NoRefundPeriod
        date={cancellationPenalties[cancellationPenalties.length - 1].end}
        totalOfferPrice={totalPrice}
        currency={cancellationPenalties[0].currency}
        displayCurrencyCode={displayCurrencyCode}
        hotelTimeZone={hotelTimeZone}
        isAuthenticateLater={isAuthenticateLater}
        renderTotalPrice={renderTotalPrice}
      />
    </>
  )
}

const PenaltyDisclaimer = ({refundType}: {refundType: RefundType}) => {
  return (
    <Box display="flex" gap="s200" paddingTop="s300">
      {refundType !== RefundType.nonRefundable && (
        <DisclaimerIcon>
          <Icon
            name="Information"
            colorPath="content.neutral.secondary"
            size="sm"
          />
        </DisclaimerIcon>
      )}

      <div>
        {refundType !== RefundType.nonRefundable && (
          <DisclaimerInfoWrapper>
            <Text variant="bodyS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper
                id="cancellationPenalties.propertyTimezone"
                defaultMessage="Dates and times refer to property’s local time."
              />
            </Text>
          </DisclaimerInfoWrapper>
        )}
      </div>
    </Box>
  )
}

export const CancellationPenalties = ({
  cancellationPenalties,
  totalPrice,
  displayCurrencyCode,
  hotelTimeZone,
  hasChargeLaterRefundPolicy,
  isPostBooking = false,
  isAuthenticateLater = false,
  renderTotalPrice
}: CancellationPenaltiesProps) => {
  const refundType = getRefundType(cancellationPenalties)

  return (
    <div data-id="cancellationPenaltiesComponent">
      <PenaltyHeader
        refundType={refundType}
        totalPrice={totalPrice}
        currencyCode={displayCurrencyCode}
        renderTotalPrice={renderTotalPrice}
      />

      <PenaltyList
        refundType={refundType}
        cancellationPenalties={cancellationPenalties}
        totalPrice={totalPrice}
        displayCurrencyCode={displayCurrencyCode}
        hotelTimeZone={hotelTimeZone}
        hasChargeLaterRefundPolicy={hasChargeLaterRefundPolicy}
        isAuthenticateLater={isAuthenticateLater}
        isPostBooking={isPostBooking}
        renderTotalPrice={renderTotalPrice}
      />

      <PenaltyDisclaimer refundType={refundType} />
    </div>
  )
}
